import React, { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Search,
  Check,
  X,
  Play,
  Zap,
  Tag,
  ArrowRight,
  Sparkles,
  Eye,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FlaskConical,
  Activity,
  ScanSearch,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// Types
interface SemanticGap {
  id: string;
  site_id: string;
  site_name: string;
  sector_id: number;
  category_path: string;
  attribute_name: string;
  technology_value: string;
  product_count: number;
  suggested_semantic_value: string | null;
  suggestion_reasoning: string | null;
  suggestion_confidence: number | null;
  suggested_at: string | null;
  status: 'pending' | 'suggested' | 'approved' | 'modified' | 'rejected' | 'applied' | 'researching';
  final_semantic_value: string | null;
  reviewed_by: string | null;
  reviewer_email: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  applied_at: string | null;
  created_at: string;
  gap_type: 'value_mapping' | 'attribute_research';
  suggested_research_data: any;
}

interface GapStats {
  pending?: number;
  suggested?: number;
  needs_expert?: number;
  approved?: number;
  modified?: number;
  rejected?: number;
  applied?: number;
}

interface CategoryHealth {
  categoryName: string;
  fullCategoryPath: string;
  productCount: number;
  avgFeedSpecs: number;
  canonicalAttributeCount: number;
  avgExtractedAttributes: number;
  specCoveragePct: number;
  pendingDiscoveryGaps: number;
  status: 'healthy' | 'needs_attention' | 'critical';
}

interface CategoryHealthSummary {
  totalCategories: number;
  criticalCount: number;
  needsAttentionCount: number;
  healthyCount: number;
}

const statusColors: Record<string, string> = {
  pending: 'bg-gray-500',
  suggested: 'bg-blue-500',
  needs_expert: 'bg-orange-500',
  approved: 'bg-green-500',
  modified: 'bg-purple-500',
  rejected: 'bg-red-500',
  applied: 'bg-emerald-600',
  researching: 'bg-cyan-500',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  suggested: 'Awaiting Review',
  needs_expert: 'Needs Expert',
  approved: 'Approved',
  modified: 'Modified',
  rejected: 'Rejected',
  applied: 'Applied',
  researching: 'Researching',
};

// Valid semantic values by attribute (should match backend)
const validSemanticValues: Record<string, string[]> = {
  cushioning: ['maximum', 'high', 'moderate', 'minimal', 'responsive'],
  midsole_tech: ['maximum', 'high', 'moderate', 'minimal'],
  outsole_material: ['premium_rubber', 'trail_rubber', 'standard_rubber', 'durable_rubber', 'minimal_rubber', 'lightweight_foam'],
  support_type: ['neutral', 'stability', 'motion_control'],
  terrain: ['road', 'trail', 'indoor', 'mixed', 'track'],
};

export default function SemanticGapsDashboard() {
  const { selectedSite } = useSite();
  const [gaps, setGaps] = useState<SemanticGap[]>([]);
  const [stats, setStats] = useState<GapStats>({});
  const [total, setTotal] = useState(0);
  const [enrichment, setEnrichment] = useState<{ totalProducts: number; enrichedProducts: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [attributeFilter, setAttributeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [gapTypeFilter, setGapTypeFilter] = useState<string>('all');
  const [attributes, setAttributes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // Workflow guide
  const [showWorkflowGuide, setShowWorkflowGuide] = useState<boolean>(() => {
    return localStorage.getItem('semantic-gaps-guide-dismissed') !== 'true';
  });

  // Selection
  const [selectedGaps, setSelectedGaps] = useState<Set<string>>(new Set());

  // Async job tracking
  const [activeJob, setActiveJob] = useState<{
    jobId: string;
    jobType: string;
    status: string;
    progress: { percent: number; message: string };
  } | null>(null);

  // Category health tab
  const [categoryHealth, setCategoryHealth] = useState<CategoryHealth[]>([]);
  const [healthSummary, setHealthSummary] = useState<CategoryHealthSummary>({ totalCategories: 0, criticalCount: 0, needsAttentionCount: 0, healthyCount: 0 });
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [scanningCategory, setScanningCategory] = useState<string | null>(null);

  // Review dialog
  const [reviewingGap, setReviewingGap] = useState<SemanticGap | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'approve' | 'modify' | 'reject'>('approve');
  const [modifiedValue, setModifiedValue] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Fetch gaps
  const fetchGaps = useCallback(async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('siteId', selectedSite.id);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (attributeFilter !== 'all') params.set('attribute', attributeFilter);
      if (categoryFilter !== 'all') params.set('categoryPath', categoryFilter);
      if (gapTypeFilter !== 'all') params.set('gapType', gapTypeFilter);
      params.set('limit', '100');

      const response = await authFetch(`${API_URL}/semantic-gaps?${params}`);
      const data = await response.json();

      setGaps(data.gaps || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
      if (data.enrichment) setEnrichment(data.enrichment);
    } catch (err: any) {
      setError(err.message || 'Failed to load semantic gaps');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id, statusFilter, attributeFilter, categoryFilter, gapTypeFilter]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    if (!selectedSite?.id) return;

    try {
      const [attrsRes, catsRes] = await Promise.all([
        authFetch(`${API_URL}/semantic-gaps/attributes?siteId=${selectedSite.id}`),
        authFetch(`${API_URL}/semantic-gaps/categories?siteId=${selectedSite.id}`),
      ]);

      const attrsData = await attrsRes.json();
      const catsData = await catsRes.json();

      setAttributes(attrsData.attributes || []);
      setCategories(catsData.categories || []);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, [selectedSite?.id]);

  // Fetch category health
  const fetchCategoryHealth = useCallback(async () => {
    if (!selectedSite?.id) return;

    setHealthLoading(true);
    setHealthError(null);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/category-health/${selectedSite.id}`);
      const data = await response.json();

      setCategoryHealth(data.categories || []);
      setHealthSummary(data.summary || { totalCategories: 0, criticalCount: 0, needsAttentionCount: 0, healthyCount: 0 });
    } catch (err: any) {
      setHealthError(err.message || 'Failed to load category health');
    } finally {
      setHealthLoading(false);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    fetchGaps();
    fetchFilterOptions();
    fetchCategoryHealth();
  }, [fetchGaps, fetchFilterOptions, fetchCategoryHealth]);

  // Page load recovery: check for active semantic_gap job
  // Also clears activeJob when switching sites
  useEffect(() => {
    if (!selectedSite?.id) {
      setActiveJob(null);
      return;
    }
    setActiveJob(null); // Clear previous site's job immediately
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/semantic-gaps/job-status/${selectedSite.id}`);
        const data = await res.json();
        setActiveJob(data.activeJob || null);
      } catch {
        // ignore - no active job
      }
    })();
  }, [selectedSite?.id]);

  // Job polling effect
  useEffect(() => {
    if (!activeJob) return;

    const interval = setInterval(async () => {
      try {
        const res = await authFetch(`${API_URL}/jobs/${activeJob.jobId}`);
        const data = await res.json();

        if (data.status === 'completed') {
          setActiveJob(null);
          fetchGaps();
          fetchFilterOptions();
          fetchCategoryHealth();
        } else if (data.status === 'failed') {
          setActiveJob(null);
          setError(data.error || 'Job failed');
        } else {
          setActiveJob(prev => prev ? {
            ...prev,
            status: data.status,
            progress: data.progress || prev.progress,
          } : null);
        }
      } catch {
        // polling error, keep trying
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeJob?.jobId]);

  // Discover gaps (async)
  const handleDiscover = async () => {
    if (!selectedSite?.id) return;

    setError(null);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/discover/${selectedSite.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.success && data.jobId) {
        setActiveJob({
          jobId: data.jobId,
          jobType: 'semantic_gap.discover',
          status: 'processing',
          progress: { percent: 0, message: 'Starting discovery...' },
        });
      } else {
        throw new Error(data.error || 'Discovery failed');
      }
    } catch (err: any) {
      setError(err.message || 'Discovery failed');
    }
  };

  // Generate suggestions (async, with optional web validation)
  const handleSuggest = async (validate = false) => {
    if (!selectedSite?.id) return;

    setError(null);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          validate,
        }),
      });

      const data = await response.json();

      if (data.success && data.jobId) {
        setActiveJob({
          jobId: data.jobId,
          jobType: 'semantic_gap.suggest',
          status: 'processing',
          progress: { percent: 0, message: 'Starting suggestions...' },
        });
      } else {
        throw new Error(data.error || 'Suggestion failed');
      }
    } catch (err: any) {
      setError(err.message || 'Suggestion failed');
    }
  };

  // Apply approved mappings for ALL categories (async)
  const handleApply = async () => {
    if (!selectedSite?.id) return;

    const approvedCount = (stats.approved || 0) + (stats.modified || 0);
    if (approvedCount === 0) {
      alert('No approved mappings to apply');
      return;
    }

    setError(null);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/apply-all/${selectedSite.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success && data.jobId) {
        setActiveJob({
          jobId: data.jobId,
          jobType: 'semantic_gap.apply_all',
          status: 'processing',
          progress: { percent: 0, message: 'Starting apply...' },
        });
      } else {
        throw new Error(data.error || 'Apply failed');
      }
    } catch (err: any) {
      setError(err.message || 'Apply failed');
    }
  };

  // Approve ALL suggested gaps for the site (async)
  const handleApproveAll = async () => {
    if (!selectedSite?.id) return;

    const suggestedCount = stats.suggested || 0;
    if (suggestedCount === 0) return;

    if (!confirm(`Approve all ${suggestedCount} suggested mappings?`)) return;

    setError(null);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/approve-all/${selectedSite.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success && data.jobId) {
        setActiveJob({
          jobId: data.jobId,
          jobType: 'semantic_gap.approve_all',
          status: 'processing',
          progress: { percent: 0, message: 'Starting bulk approval...' },
        });
      } else {
        throw new Error(data.error || 'Approve all failed');
      }
    } catch (err: any) {
      setError(err.message || 'Approve all failed');
    }
  };

  // Scan for attribute gaps (per category or all)
  const handleScanAttributes = async (categoryPath?: string) => {
    if (!selectedSite?.id) return;

    setScanningCategory(categoryPath || '__all__');
    setHealthError(null);

    try {
      const url = categoryPath
        ? `${API_URL}/semantic-gaps/discover-attributes/${selectedSite.id}?categoryPath=${encodeURIComponent(categoryPath)}`
        : `${API_URL}/semantic-gaps/discover-attributes/${selectedSite.id}`;

      const response = await authFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.success && data.jobId) {
        setActiveJob({
          jobId: data.jobId,
          jobType: 'semantic_gap.discover_attributes',
          status: 'processing',
          progress: { percent: 0, message: 'Starting attribute scan...' },
        });
      } else {
        throw new Error(data.error || 'Scan failed');
      }
    } catch (err: any) {
      setHealthError(err.message || 'Scan failed');
    } finally {
      setScanningCategory(null);
    }
  };

  // Submit review
  const handleReviewSubmit = async () => {
    if (!reviewingGap) return;

    setSubmittingReview(true);

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/${reviewingGap.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision: reviewDecision,
          finalValue: reviewDecision === 'modify' ? modifiedValue : undefined,
          notes: reviewNotes || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setReviewingGap(null);
        setReviewDecision('approve');
        setModifiedValue('');
        setReviewNotes('');
        fetchGaps();
      } else {
        throw new Error(data.error || 'Review failed');
      }
    } catch (err: any) {
      alert(err.message || 'Review failed');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Bulk review
  const handleBulkReview = async (decision: 'approve' | 'reject') => {
    if (selectedGaps.size === 0) {
      alert('No gaps selected');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/semantic-gaps/bulk-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gapIds: Array.from(selectedGaps),
          decision,
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert(`Bulk review complete: ${data.updated} updated, ${data.failed} failed`);
        setSelectedGaps(new Set());
        fetchGaps();
      } else {
        throw new Error(data.error || 'Bulk review failed');
      }
    } catch (err: any) {
      alert(err.message || 'Bulk review failed');
    }
  };

  // Toggle selection
  const toggleSelection = (gapId: string) => {
    const newSelection = new Set(selectedGaps);
    if (newSelection.has(gapId)) {
      newSelection.delete(gapId);
    } else {
      newSelection.add(gapId);
    }
    setSelectedGaps(newSelection);
  };

  // Select all suggested
  const selectAllSuggested = () => {
    const suggested = gaps.filter(g => g.status === 'suggested').map(g => g.id);
    setSelectedGaps(new Set(suggested));
  };

  // Open review dialog
  const openReview = (gap: SemanticGap) => {
    setReviewingGap(gap);
    setReviewDecision('approve');
    setModifiedValue(gap.suggested_semantic_value || '');
    setReviewNotes('');
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site from the header to view semantic gaps.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const totalStats = Object.values(stats).reduce((a, b) => a + (b || 0), 0);
  const pendingCount = (stats.pending || 0) + (stats.suggested || 0);
  const approvedCount = (stats.approved || 0) + (stats.modified || 0);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6" />
            Semantic Gap Queue
          </h1>
          <p className="text-muted-foreground">
            Map brand technologies to semantic values for accurate product enrichment
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      {activeJob && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                <span className="text-sm font-medium">
                  {activeJob.jobType === 'semantic_gap.discover' && 'Discovering gaps...'}
                  {activeJob.jobType === 'semantic_gap.suggest' && 'Generating suggestions...'}
                  {activeJob.jobType === 'semantic_gap.approve_all' && 'Approving all suggested...'}
                  {activeJob.jobType === 'semantic_gap.apply_all' && 'Applying mappings...'}
                  {activeJob.jobType === 'semantic_gap.discover_attributes' && 'Scanning for attribute gaps...'}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">{activeJob.progress.percent}%</span>
            </div>
            <Progress value={activeJob.progress.percent} className="h-2" />
            {activeJob.progress.message && (
              <p className="text-xs text-muted-foreground mt-1">{activeJob.progress.message}</p>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="mappings">
        <TabsList>
          <TabsTrigger value="mappings">
            <Tag className="h-4 w-4 mr-2" />
            Technology Mappings
          </TabsTrigger>
          <TabsTrigger value="category-health">
            <Activity className="h-4 w-4 mr-2" />
            Category Health
            {healthSummary.criticalCount > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">{healthSummary.criticalCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mappings" className="space-y-6">
      {/* Mappings tab action bar */}
      <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleDiscover}
            disabled={!!activeJob}
          >
            <Search className="h-4 w-4 mr-2" />
            Discover
          </Button>
          <div className="flex">
            <Button
              variant="outline"
              onClick={() => handleSuggest(false)}
              disabled={!!activeJob || (stats.pending || 0) === 0}
              className="rounded-r-none"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Suggest
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSuggest(true)}
              disabled={!!activeJob || (stats.pending || 0) === 0}
              className="rounded-l-none border-l-0 px-2"
              title="Suggest with web research validation (auto-approves high-confidence mappings)"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={handleApproveAll}
            disabled={!!activeJob || (stats.suggested || 0) === 0}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Approve All ({stats.suggested || 0})
          </Button>
          <Button
            onClick={handleApply}
            disabled={!!activeJob || approvedCount === 0}
          >
            <Play className="h-4 w-4 mr-2" />
            Apply All
          </Button>
          <Button variant="ghost" size="icon" onClick={() => fetchGaps()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
      </div>

      {/* Workflow Guide */}
      <Card className="border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => {
          setShowWorkflowGuide(!showWorkflowGuide);
          if (showWorkflowGuide) localStorage.setItem('semantic-gaps-guide-dismissed', 'true');
          else localStorage.removeItem('semantic-gaps-guide-dismissed');
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-500" />
              <CardTitle className="text-sm">How to use this tool</CardTitle>
            </div>
            {showWorkflowGuide ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        </CardHeader>
        {showWorkflowGuide && (
          <CardContent className="text-sm space-y-4">
            <p className="text-muted-foreground">
              This tool ensures product enrichment uses accurate semantic values. It detects two types of gaps
              and guides you through resolving them before enrichment runs.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-violet-500" />
                  <span className="font-medium">Value Mappings</span>
                </div>
                <p className="text-muted-foreground text-xs">
                  Brand technologies (e.g., "BOOST", "LIGHTSTRIKE") that need mapping to semantic values
                  (e.g., "maximum", "moderate" cushioning). Without mappings, enrichment can't interpret
                  what these technologies mean for buyers.
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="h-4 w-4 text-cyan-500" />
                  <span className="font-medium">Attribute Research</span>
                </div>
                <p className="text-muted-foreground text-xs">
                  Product attributes (e.g., "power_accuracy", "connectivity") found in data but with no
                  spec_semantics research. Without research, content generation doesn't know how to explain
                  these attributes to buyers.
                </p>
              </div>
            </div>

            <div>
              <p className="font-medium mb-2">Workflow — run these steps in order:</p>
              <ol className="space-y-2">
                <li className="flex items-start gap-3">
                  <Badge className="bg-gray-500 shrink-0 mt-0.5">1</Badge>
                  <div>
                    <span className="font-medium">Discover</span>
                    <span className="text-muted-foreground"> — Scans all products for unmapped technologies and under-researched attributes. Creates "Pending" entries in the queue.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Badge className="bg-blue-500 shrink-0 mt-0.5">2</Badge>
                  <div>
                    <span className="font-medium">Suggest</span>
                    <span className="text-muted-foreground"> — AI generates proposed mappings/research for each pending gap. Use the </span>
                    <Search className="h-3 w-3 inline" />
                    <span className="text-muted-foreground"> button to add web validation (auto-approves high-confidence results).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Badge className="bg-green-500 shrink-0 mt-0.5">3</Badge>
                  <div>
                    <span className="font-medium">Review</span>
                    <span className="text-muted-foreground"> — Check suggestions and approve, modify, or reject each one. Use "Approve All" for bulk approval of confident suggestions.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Badge className="bg-emerald-600 shrink-0 mt-0.5">4</Badge>
                  <div>
                    <span className="font-medium">Apply</span>
                    <span className="text-muted-foreground"> — Writes approved mappings to category research data. Value mappings go to attribute_equivalences; attribute research goes to spec_semantics.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Badge variant="outline" className="shrink-0 mt-0.5">5</Badge>
                  <div>
                    <span className="font-medium">Enrich</span>
                    <span className="text-muted-foreground"> — Now run product enrichment. Products will benefit from the new mappings and research you just applied.</span>
                  </div>
                </li>
              </ol>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-7 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalStats}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-500">Awaiting Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.suggested || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-500">Needs Expert</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.needs_expert || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-500">Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats.approved || 0) + (stats.modified || 0)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-500">Rejected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.rejected || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-emerald-600">Applied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.applied || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Enrichment Warning */}
      {enrichment && enrichment.totalProducts > 0 && enrichment.enrichedProducts === 0 && (
        <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>No enriched products.</strong> This site has {enrichment.totalProducts.toLocaleString()} products but none have been enriched yet.
            Discover cannot find technology mappings without extracted attributes. Run product enrichment first.
          </AlertDescription>
        </Alert>
      )}
      {enrichment && enrichment.totalProducts > 0 && enrichment.enrichedProducts > 0 && enrichment.enrichedProducts < enrichment.totalProducts * 0.1 && (
        <Alert className="border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-950 [&>svg]:text-amber-500">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Only {enrichment.enrichedProducts.toLocaleString()} of {enrichment.totalProducts.toLocaleString()} products ({Math.round(enrichment.enrichedProducts / enrichment.totalProducts * 100)}%) have been enriched.
            Discover results may be incomplete.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not_approved">Not Approved</SelectItem>
                  <SelectItem value="needs_review">Needs Review</SelectItem>
                  <SelectItem value="needs_expert">Needs Expert</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="suggested">Awaiting Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="modified">Modified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="applied">Applied</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Attribute</Label>
              <Select value={attributeFilter} onValueChange={setAttributeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All attributes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Attributes</SelectItem>
                  {attributes.map(attr => (
                    <SelectItem key={attr} value={attr}>{attr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-64">
              <Label className="text-xs">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Gap Type</Label>
              <Select value={gapTypeFilter} onValueChange={setGapTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="value_mapping">Value Mappings</SelectItem>
                  <SelectItem value="attribute_research">Attribute Research</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Bulk Actions */}
      {selectedGaps.size > 0 && (
        <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
          <span className="text-sm font-medium">{selectedGaps.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => handleBulkReview('approve')}>
            <Check className="h-4 w-4 mr-1" /> Approve All
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkReview('reject')}>
            <X className="h-4 w-4 mr-1" /> Reject All
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedGaps(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* Gap Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Technology Mappings</CardTitle>
            {(stats.suggested || 0) > 0 && (
              <Button variant="link" size="sm" onClick={selectAllSuggested}>
                Select all awaiting review
              </Button>
            )}
          </div>
          <CardDescription>
            {loading ? 'Loading...' : `${total} technologies found`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : gaps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No semantic gaps found. Click "Discover" to scan for unmapped technologies.
            </div>
          ) : (
            <div className="space-y-2">
              {gaps.map(gap => (
                <div
                  key={gap.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50"
                >
                  {/* Checkbox */}
                  {gap.status === 'suggested' && (
                    <Checkbox
                      checked={selectedGaps.has(gap.id)}
                      onCheckedChange={() => toggleSelection(gap.id)}
                    />
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {gap.gap_type === 'attribute_research' ? (
                        <>
                          <FlaskConical className="h-4 w-4 text-cyan-500" />
                          <span className="font-mono font-medium text-lg">{gap.attribute_name}</span>
                          <Badge className="bg-cyan-500" variant="secondary">Research Needed</Badge>
                        </>
                      ) : (
                        <span className="font-mono font-medium text-lg">{gap.technology_value}</span>
                      )}
                      <Badge className={statusColors[gap.status]} variant="secondary">
                        {statusLabels[gap.status]}
                      </Badge>
                      {gap.gap_type !== 'attribute_research' && (
                        <Badge variant="outline">{gap.attribute_name}</Badge>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {gap.product_count} products
                      </span>
                    </div>

                    {/* Attribute research: show sample values */}
                    {gap.gap_type === 'attribute_research' && gap.suggested_research_data && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        {(() => {
                          const data = typeof gap.suggested_research_data === 'string'
                            ? JSON.parse(gap.suggested_research_data) : gap.suggested_research_data;
                          const samples = data?.sample_values;
                          if (Array.isArray(samples)) {
                            return <span>Sample values: {samples.join(', ')}</span>;
                          }
                          // After suggestion, data becomes the spec_semantics entry
                          const keys = Object.keys(data);
                          if (keys.length > 0 && data[keys[0]]?.description) {
                            return <span>{data[keys[0]].description}</span>;
                          }
                          return null;
                        })()}
                      </div>
                    )}

                    {/* Suggestion (value mapping) */}
                    {gap.gap_type !== 'attribute_research' && gap.suggested_semantic_value && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <ArrowRight className="h-4 w-4 text-blue-500" />
                        <span className="font-medium text-blue-600">{gap.suggested_semantic_value}</span>
                        {gap.suggestion_confidence && (
                          <span className="text-muted-foreground">
                            ({Math.round(gap.suggestion_confidence * 100)}% confidence)
                          </span>
                        )}
                      </div>
                    )}

                    {/* Reasoning */}
                    {gap.suggestion_reasoning && (
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {gap.suggestion_reasoning}
                      </p>
                    )}

                    {/* Final value (value mapping only) */}
                    {gap.gap_type !== 'attribute_research' && gap.final_semantic_value && gap.status !== 'suggested' && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="font-medium text-green-600">{gap.final_semantic_value}</span>
                        {gap.reviewer_email && (
                          <span className="text-muted-foreground">by {gap.reviewer_email}</span>
                        )}
                      </div>
                    )}

                    {/* Category */}
                    <div className="mt-2 text-xs text-muted-foreground">
                      {gap.category_path}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {gap.status === 'suggested' && (
                      <Button size="sm" onClick={() => openReview(gap)}>
                        <Eye className="h-4 w-4 mr-1" /> Review
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      </TabsContent>

        <TabsContent value="category-health" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{healthSummary.totalCategories}</div>
              </CardContent>
            </Card>
            <Card className="border-red-200 dark:border-red-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-500">Critical</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-500">{healthSummary.criticalCount}</div>
                <p className="text-xs text-muted-foreground">{"<"}20% coverage</p>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 dark:border-yellow-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-yellow-600">Needs Attention</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{healthSummary.needsAttentionCount}</div>
                <p className="text-xs text-muted-foreground">20-50% coverage</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 dark:border-green-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-500">Healthy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">{healthSummary.healthyCount}</div>
                <p className="text-xs text-muted-foreground">{">"}50% coverage</p>
              </CardContent>
            </Card>
          </div>

          {/* Health Error */}
          {healthError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{healthError}</AlertDescription>
            </Alert>
          )}

          {/* Category Health Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Category Health</CardTitle>
                  <CardDescription>
                    Feed spec coverage vs extracted attributes per category
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleScanAttributes()}
                    disabled={!!activeJob || scanningCategory === '__all__'}
                  >
                    {scanningCategory === '__all__' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ScanSearch className="h-4 w-4 mr-2" />
                    )}
                    Scan All
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => fetchCategoryHealth()}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {healthLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : categoryHealth.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No categories found. Make sure the site has imported product feed data.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Products</TableHead>
                      <TableHead className="text-right">Feed Specs</TableHead>
                      <TableHead className="text-right">Canonical</TableHead>
                      <TableHead className="text-right">Extracted</TableHead>
                      <TableHead className="text-right">Coverage</TableHead>
                      <TableHead className="text-right">Pending Gaps</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryHealth.map((cat) => (
                      <TableRow key={cat.categoryName}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{cat.categoryName}</div>
                            {cat.fullCategoryPath !== cat.categoryName && (
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={cat.fullCategoryPath}>
                                {cat.fullCategoryPath}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{cat.productCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono">{cat.avgFeedSpecs}</TableCell>
                        <TableCell className="text-right font-mono">{cat.canonicalAttributeCount}</TableCell>
                        <TableCell className="text-right font-mono">{cat.avgExtractedAttributes}</TableCell>
                        <TableCell className="text-right">
                          <span className={
                            cat.specCoveragePct > 50 ? 'text-green-600 font-medium' :
                            cat.specCoveragePct > 20 ? 'text-yellow-600 font-medium' :
                            'text-red-600 font-medium'
                          }>
                            {cat.specCoveragePct}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {cat.pendingDiscoveryGaps > 0 ? (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              {cat.pendingDiscoveryGaps}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              cat.status === 'healthy' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                              cat.status === 'needs_attention' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                              'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                            }
                          >
                            {cat.status === 'healthy' ? 'Healthy' :
                             cat.status === 'needs_attention' ? 'Needs Attention' :
                             'Critical'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleScanAttributes(cat.categoryName)}
                            disabled={!!activeJob || scanningCategory === cat.categoryName}
                          >
                            {scanningCategory === cat.categoryName ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ScanSearch className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={!!reviewingGap} onOpenChange={(open) => !open && setReviewingGap(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review: "{reviewingGap?.technology_value}"</DialogTitle>
            <DialogDescription>
              Attribute: {reviewingGap?.attribute_name} | Category: {reviewingGap?.category_path}
              <br />
              Product Count: {reviewingGap?.product_count}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* AI Suggestion */}
            {reviewingGap?.suggested_semantic_value && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">AI Suggestion</span>
                  {reviewingGap.suggestion_confidence && (
                    <Badge variant="outline">
                      {Math.round(reviewingGap.suggestion_confidence * 100)}% confidence
                    </Badge>
                  )}
                </div>
                <p className="font-mono text-lg text-blue-600 dark:text-blue-400">
                  {reviewingGap.suggested_semantic_value}
                </p>
                {reviewingGap.suggestion_reasoning && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {reviewingGap.suggestion_reasoning}
                  </p>
                )}
              </div>
            )}

            {/* Decision */}
            <div className="space-y-3">
              <Label>Your Decision</Label>
              <RadioGroup value={reviewDecision} onValueChange={(v) => setReviewDecision(v as any)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="approve" id="approve" />
                  <Label htmlFor="approve" className="font-normal">
                    Approve "{reviewingGap?.suggested_semantic_value}"
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="modify" id="modify" />
                  <Label htmlFor="modify" className="font-normal">
                    Modify to different value
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reject" id="reject" />
                  <Label htmlFor="reject" className="font-normal">
                    Reject (no mapping needed)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Modified value selector */}
            {reviewDecision === 'modify' && reviewingGap && (
              <div className="space-y-2">
                <Label>Select semantic value</Label>
                <Select value={modifiedValue} onValueChange={setModifiedValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a value" />
                  </SelectTrigger>
                  <SelectContent>
                    {(validSemanticValues[reviewingGap.attribute_name] || ['high', 'moderate', 'low']).map(val => (
                      <SelectItem key={val} value={val}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this decision..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewingGap(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={submittingReview || (reviewDecision === 'modify' && !modifiedValue)}
            >
              {submittingReview ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Submit Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
